import React, { useEffect, useRef } from "react";

declare global {
  interface Window {
    turnstile?: {
      render: (
        container: HTMLElement | string,
        options: {
          sitekey: string;
          callback?: (token: string) => void;
          "expired-callback"?: () => void;
          "error-callback"?: () => void;
          theme?: "light" | "dark" | "auto";
          size?: "normal" | "compact" | "flexible";
        }
      ) => string;
      remove: (widgetId: string) => void;
      reset: (widgetId?: string) => void;
    };
  }
}

interface OcTurnstileWidgetProps {
  siteKey: string;
  onToken: (token: string) => void;
  onExpire?: () => void;
}

const OcTurnstileWidget: React.FC<OcTurnstileWidgetProps> = ({
  siteKey,
  onToken,
  onExpire,
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const widgetIdRef = useRef<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    let pollInterval: ReturnType<typeof setInterval> | null = null;
    let elapsed = 0;
    const POLL_MS = 50;
    const MAX_MS = 5000;

    const tryRender = () => {
      if (cancelled) return false;
      if (!containerRef.current) return false;
      if (!window.turnstile) return false;

      try {
        const id = window.turnstile.render(containerRef.current, {
          sitekey: siteKey,
          callback: (token: string) => {
            if (!cancelled) onToken(token);
          },
          "expired-callback": () => {
            if (!cancelled && onExpire) onExpire();
          },
          theme: "light",
          // "flexible" makes the widget fill the container's width (min 300px),
          // so it aligns visually with the input and submit button above/below.
          size: "flexible",
        });
        widgetIdRef.current = id;
        return true;
      } catch {
        return false;
      }
    };

    if (!tryRender()) {
      pollInterval = setInterval(() => {
        elapsed += POLL_MS;
        if (tryRender() || elapsed >= MAX_MS) {
          if (pollInterval) {
            clearInterval(pollInterval);
            pollInterval = null;
          }
        }
      }, POLL_MS);
    }

    return () => {
      cancelled = true;
      if (pollInterval) clearInterval(pollInterval);
      if (widgetIdRef.current && window.turnstile) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch {
          // ignore — widget may already be gone
        }
        widgetIdRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteKey]);

  return <div ref={containerRef} className="oc-turnstile-widget w-full" />;
};

export { OcTurnstileWidget };
export default OcTurnstileWidget;
